<template>
  <UiModal
    :model-value="modelValue"
    title="Уточнить сроки поставки"
    :max-width="480"
    persistent
    @update:model-value="onModelUpdate"
  >
    <p class="callback-modal__text">
      Товар «<strong>{{ productName }}</strong
      >» ({{ sku }}) сейчас отсутствует на складе.
    </p>
    <p class="callback-modal__text">
      Оставьте свой номер телефона, и менеджер свяжется с вами для уточнения сроков поставки.
    </p>
    <div class="callback-modal__field">
      <label for="callback-phone" class="callback-modal__label">Телефон</label>
      <UiInput
        id="callback-phone"
        v-model="phone"
        type="tel"
        placeholder="+7 (___) ___-__-__"
        :readonly="submitting"
      />
    </div>
    <template #footer>
      <UiButton variant="secondary" :disabled="submitting" @click="closeDialog"> Отмена </UiButton>
      <UiButton :disabled="submitting || !isPhoneValid" :loading="submitting" @click="submit">
        Отправить
      </UiButton>
    </template>
  </UiModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { apiClient } from '@/api/client'
import { useNotification } from '../composables/notification'
import UiButton from '@/shared/ui/UiButton.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiModal from '@/shared/ui/UiModal.vue'

interface Props {
  modelValue: boolean
  productName: string
  sku: string
  /** Товар, из карточки которого пришла заявка: менеджеру нужно знать, о чём звонить. */
  productId?: number | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

const notificationStore = useNotification()

const phone = ref('')
const submitting = ref(false)

const isPhoneValid = computed(() => {
  const digits = phone.value.replace(/\D/g, '')
  return digits.length >= 10
})

function onModelUpdate(value: boolean) {
  emit('update:modelValue', value)
}

function closeDialog() {
  emit('update:modelValue', false)
  emit('close')
}

async function submit() {
  if (!isPhoneValid.value) return

  submitting.value = true
  try {
    await apiClient.post('/api/callback-requests', {
      phone: phone.value,
      product_id: props.productId ?? null,
    })

    // Успех показываем только после ответа backend: раньше здесь стоял setTimeout,
    // и пользователь видел подтверждение для заявки, которой нигде не существовало.
    notificationStore.showSuccess('Запрос отправлен. Менеджер свяжется с вами в ближайшее время.')
    phone.value = ''
    closeDialog()
  } catch (error) {
    // Телефон и открытое окно сохраняем: иначе при сбое пользователю пришлось бы
    // набирать номер заново, и лид терялся бы уже по нашей вине.
    console.error('Не удалось отправить заявку на обратный звонок:', error)
    notificationStore.showError(
      error instanceof Error && error.message
        ? error.message
        : 'Не удалось отправить запрос. Проверьте связь и попробуйте ещё раз.',
    )
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.callback-modal__text {
  margin: 0 0 12px;
  line-height: 1.5;
}

.callback-modal__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.callback-modal__label {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
}
</style>
